import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';

interface TutorialStep {
  title: string;
  content: string;
  icon: string;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: 'Welcome to Blackjack Trainer!',
    content: 'This app helps you master card counting and blackjack strategy. Let\'s walk through the basics.',
    icon: '👋'
  },
  {
    title: 'Card Counting Basics',
    content: 'Card counting tracks the ratio of high to low cards remaining in the deck. When the count is high, you have an advantage!',
    icon: '🧮'
  },
  {
    title: 'The Hi-Lo System',
    content: 'Cards 2-6 = +1, Cards 7-9 = 0, Cards 10-A = -1. Keep a running count as cards are dealt.',
    icon: '📘'
  },
  {
    title: 'True Count',
    content: 'Divide the running count by decks remaining. This is your True Count - use it for bet sizing!',
    icon: '📊'
  },
  {
    title: 'Bet Sizing',
    content: 'Increase bets when TC is positive (you have edge). Minimum bet at TC ≤ 0, scale up at TC +2, +3, etc.',
    icon: '💰'
  },
  {
    title: 'Practice Modes',
    content: 'Training: Single hands. Table Sim: Full table. Speed Drill: Fast counting practice. Use all three!',
    icon: '🎯'
  },
  {
    title: 'Keyboard Shortcuts',
    content: 'H = Hit, S = Stand, D = Double, P = Split, R = Surrender, Space = Deal. Practice hands-free!',
    icon: '⌨️'
  },
  {
    title: 'You\'re Ready!',
    content: 'Start with Training mode to practice basic strategy, then graduate to Table Sim for realistic practice. Good luck!',
    icon: '🚀'
  }
];

export const Tutorial: React.FC = () => {
  const { showTutorial, dismissTutorial } = useGameStore();
  const [currentStep, setCurrentStep] = useState(0);

  if (!showTutorial) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      dismissTutorial();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleSkip = () => {
    dismissTutorial();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-700">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {TUTORIAL_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentStep 
                  ? 'bg-emerald-500 w-6' 
                  : index < currentStep 
                    ? 'bg-emerald-700' 
                    : 'bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">{step.icon}</div>
          <h2 className="text-2xl font-bold text-white mb-3">{step.title}</h2>
          <p className="text-gray-300 text-lg leading-relaxed">{step.content}</p>
        </div>

        {/* Step indicator */}
        <div className="text-center text-gray-500 text-sm mb-4">
          Step {currentStep + 1} of {TUTORIAL_STEPS.length}
        </div>

        {/* Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-white transition-colors text-sm"
          >
            Skip Tutorial
          </button>

          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
              >
                ← Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold transition-colors"
            >
              {isLastStep ? 'Start Playing! 🎰' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};




